"use client";

import { ChevronLeft } from "lucide-react";
import type * as React from "react";
import { Button } from "@/components/ui/atoms/button";
import { cn } from "@/lib/utils";

export type McpSectionNavHeaderProps = {
  /**
   * Identity slot — typically a `McpToolkitGroupHeader` or a plain title with
   * trailing count. Rendered after the back affordance and the divider.
   */
  identity: React.ReactNode;
  /**
   * Optional right-aligned actions (e.g. "Select all" / "Clear" buttons). Wraps
   * to a second row at narrow widths instead of squeezing the identity slot.
   */
  actions?: React.ReactNode;
  /**
   * Click handler for the back affordance. When provided, the default
   * "← MCP servers" button is rendered automatically.
   */
  onBack?: () => void;
  /**
   * Label for the back button (and its `aria-label`). Override to match the
   * surface being returned to — e.g. `"Tools"` or `"Results"`.
   */
  backLabel?: string;
  /**
   * Render a custom back affordance. Overrides `onBack` + `backLabel` if both
   * are passed — useful when the back action needs to be a link instead of a
   * button.
   */
  back?: React.ReactNode;
} & Omit<React.ComponentProps<"div">, "children">;

/**
 * Section navigation strip used at the top of a drill-in or review pane:
 * a back affordance, a separator, identity (avatar + title + count), and an
 * optional cluster of actions on the right. Used as the recurring header for
 * `ToolsView` and `SelectedToolsReview` inside the MCP server picker.
 *
 * Pure layout — the caller owns what goes in each slot.
 */
export function McpSectionNavHeader({
  identity,
  actions,
  back,
  onBack,
  backLabel = "MCP servers",
  className,
  ...rest
}: McpSectionNavHeaderProps) {
  const backNode =
    back ??
    (onBack ? (
      <Button
        aria-label={`Back to ${backLabel.toLowerCase()}`}
        onClick={onBack}
        size="sm"
        variant="ghost"
      >
        <ChevronLeft aria-hidden data-icon="inline-start" />
        {backLabel}
      </Button>
    ) : null);

  return (
    <div
      {...rest}
      className={cn(
        "flex flex-wrap items-center justify-between gap-x-3 gap-y-2",
        className
      )}
      data-slot="mcp-section-nav-header"
    >
      <div className="flex min-w-0 items-center gap-3">
        {backNode}
        {backNode ? (
          <span aria-hidden className="h-5 w-px shrink-0 bg-border" />
        ) : null}
        <div className="flex min-w-0 items-center gap-2">{identity}</div>
      </div>
      {actions ? (
        <div className="flex items-center gap-1">{actions}</div>
      ) : null}
    </div>
  );
}
